import React, { ReactNode, createContext, useContext, useMemo } from 'react';
import { SharedValue } from 'react-native-reanimated';

import { ScrollEventsHandlersHookType } from '@gorhom/bottom-sheet/lib/typescript/types';

import { useScrollEventsHandlersBottomSheet } from '../fantasy-event-sheets/hooks/use-scroll-events-handler-bottom-sheet';

type ScrollableModalContextValue = {
    // prop used by BottomSheetFlatList/BottomSheetScrollView to let use subscribe to scroll events
    // and update the scroll offset - needs to be passed to the list inside the bottom sheet
    sheetScrollableHook: ScrollEventsHandlersHookType;
    // the scroll offset of the list inside the bottom sheet
    // this will be used to animate content inside the bottom sheet list based on its scroll position
    scrollOffset: SharedValue<number>;
};

const ScrollableModalContext = createContext<ScrollableModalContextValue | undefined>(undefined);

type ScrollableModalContextProviderProps = {
    children: ReactNode;
};

/**
 * Provider that helps us get the scroll of the list inside the bottom sheet and provides the neccessary for the list
 */
export const ScrollableModalContextProvider: React.FC<ScrollableModalContextProviderProps> = ({ children }) => {
    const { useDefaultHook: sheetScrollableHook, scrollY: scrollOffset } = useScrollEventsHandlersBottomSheet();

    const value = useMemo(() => {
        return { sheetScrollableHook, scrollOffset };
    }, [sheetScrollableHook, scrollOffset]);
    return <ScrollableModalContext.Provider value={value}>{children}</ScrollableModalContext.Provider>;
};

export const useScrollableModalContext = (): ScrollableModalContextValue => {
    const context = useContext(ScrollableModalContext);
    if (!context) {
        throw new Error('useScrollableModalContext must be used within a ScrollableModalContextProvider');
    }
    return context;
};
